/**
 * 账号相关操作模块
 * */
import store from '@/store';
import config from '@/config';
import { getLocalStorage, setLocalStorage } from '@/common/js/mUtils';
import { login, loginByToken, loginLiuHe, logout } from '@/api';
import Qs from 'qs';

let userModel = {
  checkLoginState() {
    let userData = { oauth: {}, permissionsList: [] };
    // 如果工程配置loginType是local的话则刷新页面从本地取登陆信息，保持登陆状态
    if (
      // 删掉原有的开发环境配置
      // process.env.VUE_APP_ENV_NAME === 'development' ||
      config.loginType === 'local'
    ) {
      userData = getLocalStorage('user') || { oauth: {}, permissionsList: [] };
    } else {
      userData = store.state.user;
    }
    return !!userData.oauth.access_token;
  },

  /**
   * 登录
   * @param options
   * @param type form: 表单登录/token: token登录/ticket：OA sso单点ticket登录/ wxToken:微信小程序token登录/appAutoLogin: appToken登录
   * @param to 页面跳转路由信息
   * @returns {Promise<void>}
   */
  async doLogin(options, type, toPage) {
    let loginType = '';
    let loginFnMap = {
      token: '_doLoginByToken',
      UserCenterSso: '_doLoginRedirectToUserCenterSso', // 新增用户中心登录
    };
    // 先通过url携带的参数判断是什么类型登录
    switch (true) {
      case !!options.code:
        loginType = 'UserCenterSso'; // 如果参数带有code，则优先级最高，覆盖除appAutoLogin以外其他所有登录方式
        break;
      case !!options.token:
        loginType = 'token';
        break;
    }
    // 如果参数type不为空优先以指定type方式登录
    loginType = type || loginType;

    // 如果没有loginType，则跳转用户中心sso
    if (!loginType) {
      loginType = 'UserCenterSso';
    }

    // 有loginType执行自动登录
    let authData;
    try {
      await userModel[loginFnMap[loginType]](options, toPage);
      const isLogined = await userModel.checkLoginState();
      store.commit('UPDATE_LOGIN_STATE', isLogined);
      store.dispatch('getUserInfo');
    } catch (e) {
      userModel._loginFailed(toPage, e);
      throw new Error(e);
    }
    return authData;
  },

  /* 用token登录
   * @param data 用户access_token
   * @returns {Promise<void>}
   * @private
   */
  _doLoginByToken(data) {
    store.commit('UPDATE_OAUTH', { access_token: data.token });
    return new Promise((resolve, reject) => {
      loginByToken()
        .then((res) => {
          store.commit('UPDATE_OAUTH', res.body);
          resolve(res.body);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },

  /**
   * 从缓存里获取用户登录信息
   * @returns {Promise<void>}
   */
  async _getLoginDataFromLocal() {
    store.commit('UPDATE_USER_FROM_LOCAL');
  },

  /**
   * 通过用户中心sso的方式登录
   */
  _doLoginRedirectToUserCenterSso(data, toPage) {
    // 用户中心登录后，回调地址会携带code，用来换取用户信息
    const { code } = data;
    const localCodeObj = getLocalStorage('_localQueryCodeAdminBase') || {};
    // 如果没有code, 或者code和本地存储的code一致，则重新去用户中心sso换取新的code
    if (!code || code == localCodeObj.code) {
      // 跳转用户中心登录
      userModel._goLoginRedirectToUserCenterSso(toPage);
      return false;
    } else if (code && code != localCodeObj.code) {
      // 如果有code，且与本地存储的不一致，则用该code换取用户登录信息
      // 根据toPage决定回调地址
      const redirect_url = encodeURIComponent(
        toPage
          ? userModel._formatToPageByToObject(toPage)
          : userModel._formatToPageByLocation(window.location)
      );
      const loginApi = config.useIDM ? loginLiuHe : login;

      let params = {
        grant_type: 'authorization_code',
        code,
      };
      if (config.useIDM) {
        params = {
          ...params,
          client_id: config.ssoClientId,
          redirect_uri: redirect_url,
        };
      }
      // 换取用户信息
      return new Promise((resolve, reject) => {
        loginApi(params)
          .then((res) => {
            store.commit('UPDATE_OAUTH', res.body);
            // 存储code
            setLocalStorage(
              '_localQueryCodeAdminBase',
              JSON.stringify({
                code,
              })
            );
            resolve(res.body);
          })
          .catch((err) => {
            reject(err);
          });
      });
    }
  },
  /**
   *
   * @private
   */
  _loginFailed(toPage, err) {
    const { code, message = '该用户暂未同步到本系统/已被禁用，请稍后再试' } =
      err;
    if (code === '1030') {
      // 六和人员暂没有同步到用户中心
      store.dispatch('showMessage', {
        type: 'error',
        message: message,
        duration: 0,
      });
      return; // 不再跳转
    }
    store.dispatch('showMessage', {
      type: 'error',
      message: message,
    });
    // 跳转用户中心登录页
    userModel._goLoginRedirectToUserCenterSso(toPage);
  },

  // 格式化to对象，去掉code参数，防止去到IDM单点页面后，code一直重复
  _formatToPageByToObject(toPage = {}) {
    const { origin } = window.location;
    let { query = {}, path = 'cooperation-manage' } = toPage;
    if (path === 'no-permision' || path === '404' || !path) {
      path = 'cooperation-manage';
    }
    const { code, ...others } = query;
    const queryDataString = Qs.stringify(others);
    return queryDataString
      ? `${origin}/#${path}?${queryDataString}`
      : `${origin}/#${path}`;
  },

  // 格式化location对象，去掉code参数，防止去到IDM单点页面后，code一直重复
  _formatToPageByLocation(location = {}) {
    const { href } = location;
    let [base, query = ''] =
      href.indexOf('?') !== -1 ? href.split('?') : [href, ''];
    const queryDataParse = Qs.parse(query);
    const { code, ...others } = queryDataParse;
    const queryDataString = Qs.stringify(others);
    const path = 'cooperation-manage';
    if (base.indexOf('#') === -1) {
      base = `${base}/#/${path}`;
    } else {
      let [origin, basePath = ''] = base.split('/#/');
      if (basePath === 'no-permision' || basePath === '404' || !basePath) {
        base = `${origin}/#/${path}`;
      }
    }
    return queryDataString ? `${base}?${queryDataString}` : base;
  },

  /**
   * 跳转用户中心登录页
   */
  async _goLoginRedirectToUserCenterSso(toPage) {
    // 将本地存储的用户登录信息清空
    await store.dispatch('clearUserAuthInfo');
    const client_id = config.ssoClientId;

    // 根据toPage决定回调地址
    const redirect_url = encodeURIComponent(window.location.origin);
    const uuid = userModel._uuidBulid();
    localStorage.removeItem('uuid-hash')
    let hash=window.location.hash
    if(!hash){
      hash='#/cooperation-manage/home'
    }
    if(hash==='#/'){
      hash='#/cooperation-manage/home'
    }
    localStorage.setItem('uuid-hash',JSON.stringify({
      [uuid]:hash
    }))
    window.location.href = `${config.newSsoLoginUrl}?client_id=${client_id}&scope=openid&response_type=code&state=${uuid}&redirect_uri=${redirect_url+'/'}`;

  },

  /**
   * 退出登录用户中心页面
   */
  _goLogoutRedirectToUserCenter(toPage) {
    logout().then(async () => {
      await store.dispatch('clearUserAuthInfo');
      // 根据toPage决定回调地址
      const redirect_url = encodeURIComponent(window.location.origin);
      if (!config.useIDM) {
        // 用户中心退出页面
        const client_id = config.ssoClientId;
        const login_redirect_url = `${config.newSsoLoginUrl}?client_id=${client_id}&response_type=code&redirect_uri=${redirect_url}`;
        window.location.href = `${config.newUserCenterLogoutUrl}?client_id=${
          config.ssoClientId
        }&response_type=code&redirect_uri=${encodeURIComponent(
          login_redirect_url
        )}`;
      } else {
        const uuid = userModel._uuidBulid();
        localStorage.removeItem('uuid-hash')
        let hash=window.location.hash
        if(!hash){
          hash='#/cooperation-manage/home'
        }
        if(hash==='#/'){
          hash='#/cooperation-manage/home'
        }
        localStorage.setItem('uuid-hash',JSON.stringify({
          [uuid]:hash
        }))

        window.location.href =  `${config.newUserCenterLogoutUrl}?client_id=${config.ssoClientId}&scope=openid&response_type=code&state=${uuid}&redirect_uri=${redirect_url+'/'}`;

      }
    });
  },
  _uuidBulid(){
    const s = [];
    const hexDigits = '0123456789abcdef';
    for (let i = 0; i < 36; i++) {
      s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
    }
    s[14] = '4'; // bits 12-15 of the time_hi_and_version field to 0010
    s[19] = hexDigits.substr((s[19] & 0x3) | 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
    s[8] = s[13] = s[18] = s[23] = '-';

    const uuid = s.join('');
    return uuid;
  }

};

export default userModel;
